import { useT } from '../../i18n';
import { L } from '../tokens';
import { useViewportKind } from '../useViewport';
import { Message, Notes, PeriodPills, ReportPage } from './ReportParts';
import { periodBody } from './reportsApi';
import { countText, customerName, moneyText } from './reportFormat';
import { useReportPeriod } from './periodStore';
import { useReport } from './useReport';
import { DocumentsCard, MoneyPair, ProblemsCard, RankingTable } from './WhoParts';

const KINDS = ['this_week', 'this_month', 'this_year', 'custom'];

// Leo's "Report: customers" (updated board): the same skeleton as Suppliers without commission. Orders, Documents, Discrepancies identified, Documents late and Price increases, and the
// customers ranked by order value. "Without a customer" shows the orders and invoices that cannot be tied to a customer yet, so the ranking plus this line adds up to the Suppliers report.
export default function CustomersView() {
  const t = useT();
  const phone = useViewportKind() === 'phone';
  const [period, setPeriod] = useReportPeriod('past');
  const { status, data, retry } = useReport('customers', periodBody(period));
  const two = { display: 'grid', gridTemplateColumns: phone ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 18 };
  const w = data && data.without_customer;
  const cell = { padding: '10px 10px', textAlign: 'right', borderBottom: `1px solid ${L.rowLine}`, whiteSpace: 'nowrap' };
  const none = w && (w.orders > 0 || w.invoices > 0) ? (
    <tr data-testid="cust-without">
      <td style={{ ...cell, textAlign: 'left', color: L.muted }}>{t('reports.customers.without')}</td><td style={cell} />
      <td style={cell} data-testid="cust-without-orders">{countText(w.orders)}</td>
      <td style={cell} data-testid="cust-without-value">{moneyText(w.order_value, data.currency)}</td>
      <td style={{ ...cell, fontWeight: 600 }} data-testid="cust-without-invoiced">{moneyText(w.invoiced, data.currency)}</td>
    </tr>
  ) : null;
  return (
    <ReportPage title={t('reports.customers.title')} backTo="/reports" pills={<PeriodPills kinds={KINDS} value={period} onChange={setPeriod} />} testid="report-customers">
      {status !== 'ready' ? <Message status={status} onRetry={retry} /> : (
        <>
          <MoneyPair data={data} prefix="cus" phone={phone} />
          <div style={two}>
            <DocumentsCard documents={data.documents} prefix="cus" />
            <ProblemsCard problems={data.problems} currency={data.currency} prefix="cus" who="customer" />
          </div>
          <RankingTable title={t('reports.customers.ranking')} testid="cust-ranking" currency={data.currency}
            columns={[{ key: 'orders', label: t('reports.who.col.orders') }, { key: 'order_value', label: t('reports.who.col.orderValue'), money: true }, { key: 'invoiced', label: t('reports.who.col.invoiced'), money: true, strong: true }]}
            rows={data.customers.map((r) => ({ id: r.customer.id, testid: `cust-row-${r.customer.id}`, name: customerName(r.customer, t), to: `/reports/customers/${r.customer.id}`, bar: r.bar, orders: r.orders, order_value: r.order_value, invoiced: r.invoiced }))}
            noteRow={none} />
          {none && <p data-testid="cust-without-note" style={{ margin: 0, fontSize: 14, color: L.muted }}>{t('reports.customers.withoutNote')}</p>}
          <Notes notes={data.notes} />
        </>
      )}
    </ReportPage>
  );
}
